import type { TournamentSetupStatus } from "./api";

/** Torneo en borrador = asistente (Eduardo 2026-10-02): mientras el torneo no
 * se publique, ninguna salida lleva a la operación del torneo (Resumen, dock
 * «Operación del torneo»). El destino es el paso de Publicar (al final de
 * Calendario, paso 10, desde Eduardo 2026-10-05; antes al final del Draw)
 * cuando todo lo demás está listo, o el paso pendiente. */

/** `?publicar=1` en Calendario del asistente: la tarjeta «Publicar el
 * torneo» se enfoca sola si ya se puede publicar. */
export const PUBLISH_FOCUS_PARAM = "publicar";

/** Aviso fijo arriba del paso al que se regresó un borrador. */
export const DRAFT_NOTICE_TEXT = "Este torneo sigue en borrador: publícalo para empezar a operarlo.";

type DraftSetupStatus = Pick<
  TournamentSetupStatus,
  "is_incomplete_setup" | "next_setup_path" | "setup_step_reached" | "pending_setup_path"
>;

export function publishStepPath(tournamentId: number, divisionId?: string | null): string {
  const params = new URLSearchParams();
  if (divisionId) params.set("division", divisionId);
  params.set(PUBLISH_FOCUS_PARAM, "1");
  return `/admin/tournaments/${tournamentId}/setup/schedule?${params.toString()}`;
}

/** Número de paso del asistente de cada página (igual que SETUP_STEP_PATHS
 * del backend; Jugadores vive dentro de Inscripciones, paso 5). */
const WIZARD_STEP_BY_PATH: Array<[string, number]> = [
  ["/edit", 1],
  ["/setup/rules", 2],
  ["/setup/call", 3],
  ["/setup/settings", 4],
  ["/setup/registrations", 5],
  ["/setup/players/new", 5],
  ["/setup/players", 5],
  ["/setup/format", 6],
  ["/setup/ranking", 7],
  ["/setup/groups", 8],
  ["/setup/rounds", 9],
  ["/setup/schedule", 10],
];

function wizardStepOf(path: string): number | null {
  const pathname = path.split("?")[0].replace(/^\/admin\/tournaments\/\d+/, "").replace(/\/$/, "");
  return WIZARD_STEP_BY_PATH.find(([suffix]) => suffix === pathname)?.[1] ?? null;
}

/** Lo que falta para publicar, si algo falta. Con punto de reanudación
 * (`setup_step_reached`) el backend lo manda aparte en `pending_setup_path`;
 * sin él (torneos anteriores), `next_setup_path` ya es el paso pendiente. */
export function pendingSetupPath(setupStatus: DraftSetupStatus | null | undefined): string | null {
  if (!setupStatus?.is_incomplete_setup) return null;
  if (setupStatus.setup_step_reached != null) return setupStatus.pending_setup_path ?? null;
  return setupStatus.next_setup_path ?? null;
}

/** A dónde va un borrador que intenta entrar a la operación. Manda el punto
 * de reanudación del backend; si ya llegó al Draw (paso 9) o a Calendario
 * (paso 10) y nada lo impide, lo que sigue es publicar (al final de
 * Calendario). Si algo que impide publicar quedó en ese paso o antes (p. ej.
 * una categoría nueva sin formato, o un draw sin generar), va a ese paso. */
export function draftWizardDestination(
  tournamentId: number,
  setupStatus: DraftSetupStatus | null | undefined,
  divisionId?: string | null,
): string {
  const resume = setupStatus?.is_incomplete_setup ? setupStatus.next_setup_path : null;
  if (!resume) return publishStepPath(tournamentId, divisionId);
  const reached = setupStatus?.setup_step_reached != null;
  const pending = reached ? setupStatus?.pending_setup_path ?? null : null;
  const resumeStep = wizardStepOf(resume);
  const pendingStep = pending ? wizardStepOf(pending) : null;
  const next =
    pending && resumeStep !== null && pendingStep !== null && pendingStep <= resumeStep ? pending : resume;
  const [pathname, query = ""] = next.split("?");
  if (pathname.endsWith("/setup/schedule")) return publishStepPath(tournamentId, divisionId);
  if (pathname.endsWith("/setup/rounds")) {
    // Llegó al Draw y nada impide publicar: lo que sigue es Publicar, que
    // ya vive al final de Calendario. Con un draw pendiente (o sin punto de
    // reanudación, donde el Draw ES lo pendiente), al Draw de esa categoría.
    if (reached && !pending) return publishStepPath(tournamentId, divisionId);
    const params = new URLSearchParams(query);
    if (!params.has("division") && divisionId) params.set("division", divisionId);
    const search = params.toString();
    return search ? `${pathname}?${search}` : pathname;
  }
  return next;
}

/** Secciones de operación que son el MISMO paso del asistente (revisión del
 * borrador 2026-10-02): un link a Calendario (calendario del club, Inicio,
 * barra de abajo) abre el paso 10 con su categoría y fecha, no Publicar. */
const OPERATION_SECTION_TO_WIZARD: Array<[string, string]> = [
  ["/players", "/setup/players"],
  ["/players/new", "/setup/players/new"],
  ["/groups", "/setup/groups"],
  ["/rounds", "/setup/rounds"],
  ["/schedule", "/setup/schedule"],
  ["/rules", "/setup/rules"],
];

/** El paso del asistente equivalente a una sección de operación, con la misma
 * query (`?division`, `?fecha`…). Null si la sección solo tiene sentido con el
 * torneo publicado (Resumen, Resultados, Ranking…) o si ese paso aún está
 * bloqueado por algo pendiente — entonces manda `draftWizardDestination`. */
export function draftSectionDestination(
  tournamentId: number,
  pathname: string,
  search: string,
  setupStatus: DraftSetupStatus | null | undefined,
): string | null {
  const base = `/admin/tournaments/${tournamentId}`;
  if (!pathname.startsWith(`${base}/`)) return null;
  const section = pathname.slice(base.length).replace(/\/$/, "");
  const wizardPath = OPERATION_SECTION_TO_WIZARD.find(([operation]) => operation === section)?.[1];
  if (!wizardPath) return null;
  const pending = pendingSetupPath(setupStatus);
  if (pending) {
    const pendingStep = wizardStepOf(pending);
    const targetStep = wizardStepOf(wizardPath);
    if (pendingStep === null || targetStep === null || targetStep > pendingStep) return null;
  }
  return `${base}${wizardPath}${search}`;
}

/** Base de los links del «Checklist de categorías»: dentro del asistente y
 * con el torneo en borrador cada chip abre su paso (Draw, Grupos,
 * Inscripciones, Calendario) en vez de la sección de operación. Publicado,
 * los chips siguen llevando a la operación como siempre. */
export function checklistBasePath(tournamentId: number, setupMode: boolean, tournamentStatus: string | null | undefined): string {
  const base = `/admin/tournaments/${tournamentId}`;
  return setupMode && tournamentStatus === "draft" ? `${base}/setup` : base;
}
